// RITA-1525 (Teilpaket 3) — Platzhalter und Kästchen in reinem Text erkennen (Einfügen aus der Zwischenablage).
//
// `{{VORNAME}}` mit einem Schlüssel aus dem Katalog des Vertragseditors wird zum Platzhalterknoten, alles
// andere bleibt Text – auch `{{UNBEKANNT}}`, `{{vorname}}`, `{{ ORT }}` oder ein offenes `{{`. Die
// Grammatik ist dieselbe wie im bisherigen Editor und im HTML-Import (`findKnownPlaceholders`), damit
// es nur eine Schreibweise gibt. Seit RITA-1533 werden außerhalb von Platzhaltern auch die Zeichen
// ☐ (leer), ☒ und ☑ (angekreuzt) zu echten Kästchen; ein direkt folgender Darstellungswähler
// (U+FE0E/U+FE0F, Emoji-Schreibweise) gehört zum Zeichen. Ähnliche Zeichen wie □ ■ ✓ ✗ [ ] [x] bleiben
// Text. Die Erkennung läuft nur beim Einfügen aus der Zwischenablage, nicht beim Tippen (Entscheidung
// Julia 30.09.2026 für Platzhalter; für Kästchen ebenso, Kenntnis e vom 05.10.2026).
import { findKnownPlaceholders } from '../../utils/placeholderHtml';
import { KAESTCHEN_LEER } from './erzeugen';
import { BEKANNTE_VERTRAG_PLATZHALTER, VertragPlatzhalterKey } from './platzhalterkatalog';

export type Textsegment =
  | { typ: 'text'; text: string }
  | { typ: 'platzhalter'; schluessel: VertragPlatzhalterKey }
  | { typ: 'kaestchen'; angekreuzt: boolean };

// Escape-Schreibweise, damit die (teils unsichtbaren) Zeichen im Review lesbar sind:
// ☐ U+2610, ☑ U+2611, ☒ U+2612; Darstellungswähler U+FE0E/U+FE0F. Eine Fanggruppe für split()
// (kein matchAll: tsconfig-Ziel es5).
const KAESTCHEN_ZEICHEN = /([\u2610\u2611\u2612])[\uFE0E\uFE0F]?/;

/** Zerlegt Text ohne Platzhalter in Text- und Kästchensegmente; leere Textsegmente entfallen. */
function textMitKaestchen(text: string): Textsegment[] {
  return text
    .split(KAESTCHEN_ZEICHEN)
    .map(
      (stueck, index): Textsegment =>
        index % 2 === 1 ? { typ: 'kaestchen', angekreuzt: stueck !== KAESTCHEN_LEER } : { typ: 'text', text: stueck },
    )
    .filter((segment) => segment.typ !== 'text' || segment.text !== '');
}

/** Zerlegt eine Textzeile in Text-, Platzhalter- und Kästchensegmente; leere Textsegmente entfallen. */
export function textInSegmente(text: string): Textsegment[] {
  const segmente: Textsegment[] = [];
  let rest = 0;
  findKnownPlaceholders(text, BEKANNTE_VERTRAG_PLATZHALTER).forEach((treffer) => {
    if (treffer.start > rest) segmente.push(...textMitKaestchen(text.slice(rest, treffer.start)));
    segmente.push({ typ: 'platzhalter', schluessel: treffer.key as VertragPlatzhalterKey });
    rest = treffer.end;
  });
  if (rest < text.length) segmente.push(...textMitKaestchen(text.slice(rest)));
  return segmente;
}
